import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  Input,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import type { TextLimitField } from '@inference.engine/ad-formats/web';
import { textLimitRows } from '../lib/spec-rows';
import type { LibraryFormat, LibraryState, LibraryTextLimit, TextLimitInput } from '../model/types';
import { ProvenanceFields } from './ProvenanceFields';
import { emptyProvenance, type ProvenanceDraft } from './provenance';

type TFn = ReturnType<typeof useTranslation<'admin'>>['t'];

function fieldLabel(t: TFn, field: TextLimitField): string {
  switch (field) {
    case 'headline':
      return t('admin.adFormats.textLimit.fields.headline');
    case 'primaryText':
      return t('admin.adFormats.textLimit.fields.primaryText');
    case 'description':
      return t('admin.adFormats.textLimit.fields.description');
    case 'callToAction':
      return t('admin.adFormats.textLimit.fields.callToAction');
    case 'displayUrl':
      return t('admin.adFormats.textLimit.fields.displayUrl');
    case 'brandName':
      return t('admin.adFormats.textLimit.fields.brandName');
    case 'adName':
      return t('admin.adFormats.textLimit.fields.adName');
  }
}

const parseCount = (value: string): number | null | undefined =>
  value.trim() === '' ? null : /^\d+$/.test(value.trim()) ? Number(value.trim()) : undefined;

interface TextLimitsTabProps {
  state: LibraryState;
  format: LibraryFormat;
  isSaving: boolean;
  onSave: (field: string, input: TextLimitInput | null) => Promise<boolean>;
}

export function TextLimitsTab({ state, format, isSaving, onSave }: TextLimitsTabProps) {
  const { t } = useTranslation('admin');
  const [editing, setEditing] = useState<TextLimitField | null>(null);
  const rows = useMemo(() => textLimitRows(state, format), [state, format]);
  const profileName =
    state.profiles.find((profile) => profile.key === format.specProfileKey)?.name ?? '';
  const count = (value: number | null | undefined) =>
    value === null || value === undefined ? '—' : value;

  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.adFormats.textLimit.field')}</TableHead>
              <TableHead>{t('admin.adFormats.textLimit.maxChars')}</TableHead>
              <TableHead>{t('admin.adFormats.textLimit.recommendedChars')}</TableHead>
              <TableHead>{t('admin.adFormats.textLimit.truncatesAt')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(({ field, own, inherited }) => {
              const shown = own ?? inherited;
              return (
                <TableRow key={field}>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => setEditing(field)}
                      className="text-primary underline-offset-2 hover:underline"
                    >
                      {fieldLabel(t, field)}
                    </button>
                    {shown && !own && (
                      <Badge variant="secondary" className="ml-xs">
                        {t('admin.adFormats.spec.inherited', { profile: profileName })}
                      </Badge>
                    )}
                  </TableCell>
                  {shown ? (
                    <>
                      <TableCell className="tabular-nums">{count(shown.maxChars)}</TableCell>
                      <TableCell className="tabular-nums">
                        {count(shown.recommendedChars)}
                      </TableCell>
                      <TableCell className="tabular-nums">{count(shown.truncatesAt)}</TableCell>
                    </>
                  ) : (
                    <TableCell colSpan={3} className="text-muted-foreground">
                      {t('admin.adFormats.spec.notChecked')}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {editing && (
        <TextLimitSheet
          field={editing}
          label={fieldLabel(t, editing)}
          current={rows.find((row) => row.field === editing)?.own}
          isSaving={isSaving}
          onClose={() => setEditing(null)}
          onSave={(input) => {
            void onSave(editing, input).then((ok) => ok && setEditing(null));
          }}
        />
      )}
    </>
  );
}

interface TextLimitSheetProps {
  field: TextLimitField;
  label: string;
  current: LibraryTextLimit | undefined;
  isSaving: boolean;
  onClose: () => void;
  onSave: (input: TextLimitInput | null) => void;
}

function TextLimitSheet({ field, label, current, isSaving, onClose, onSave }: TextLimitSheetProps) {
  const { t } = useTranslation('admin');
  const [maxChars, setMaxChars] = useState('');
  const [recommendedChars, setRecommendedChars] = useState('');
  const [truncatesAt, setTruncatesAt] = useState('');
  const [allowed, setAllowed] = useState('');
  const [provenance, setProvenance] = useState<ProvenanceDraft>(emptyProvenance);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setMaxChars(current?.maxChars?.toString() ?? '');
    setRecommendedChars(current?.recommendedChars?.toString() ?? '');
    setTruncatesAt(current?.truncatesAt?.toString() ?? '');
    setAllowed(current?.allowedValues?.join(', ') ?? '');
    setProvenance(
      current
        ? {
            requirement: current.requirement,
            scope: current.scope,
            notes: current.notes ?? '',
          }
        : emptyProvenance(),
    );
  }, [current, field]);

  const counts = {
    maxChars: parseCount(maxChars),
    recommendedChars: parseCount(recommendedChars),
    truncatesAt: parseCount(truncatesAt),
  };
  const countInvalid = Object.values(counts).some((value) => value === undefined);
  const allowedValues = allowed
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean);

  const save = () => {
    setSubmitted(true);
    if (countInvalid) return;
    onSave({
      maxChars: counts.maxChars ?? null,
      recommendedChars: counts.recommendedChars ?? null,
      truncatesAt: counts.truncatesAt ?? null,
      allowedValues: allowedValues.length > 0 ? allowedValues : null,
      requirement: provenance.requirement,
      scope: provenance.scope,
      notes: provenance.notes.trim() || null,
    });
  };

  const countField = (
    id: string,
    title: string,
    value: string,
    setValue: (next: string) => void,
  ) => (
    <Field>
      <FieldLabel htmlFor={id}>{title}</FieldLabel>
      <Input
        id={id}
        inputMode="numeric"
        value={value}
        onChange={(event) => setValue(event.target.value)}
      />
    </Field>
  );

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex w-full flex-col gap-md overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{t('admin.adFormats.spec.edit', { property: label })}</SheetTitle>
          <SheetDescription>{t('admin.adFormats.spec.sheetDescription')}</SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-md px-md">
          <div className="grid grid-cols-3 gap-md">
            {countField(
              'limit-max',
              t('admin.adFormats.textLimit.maxChars'),
              maxChars,
              setMaxChars,
            )}
            {countField(
              'limit-recommended',
              t('admin.adFormats.textLimit.recommendedChars'),
              recommendedChars,
              setRecommendedChars,
            )}
            {countField(
              'limit-truncates',
              t('admin.adFormats.textLimit.truncatesAt'),
              truncatesAt,
              setTruncatesAt,
            )}
          </div>
          {submitted && countInvalid && (
            <FieldError>{t('admin.adFormats.textLimit.errors.number')}</FieldError>
          )}
          <Field>
            <FieldLabel htmlFor="limit-allowed">
              {t('admin.adFormats.textLimit.allowedValues')}
            </FieldLabel>
            <Input
              id="limit-allowed"
              value={allowed}
              onChange={(event) => setAllowed(event.target.value)}
            />
            <FieldDescription>{t('admin.adFormats.textLimit.allowedValuesHint')}</FieldDescription>
          </Field>
          <ProvenanceFields
            // eslint-disable-next-line i18next/no-literal-string -- element id prefix
            idPrefix="limit"
            draft={provenance}
            onChange={setProvenance}
          />
        </div>
        <SheetFooter className="flex-row justify-between">
          {current ? (
            <Button variant="ghost" disabled={isSaving} onClick={() => onSave(null)}>
              {t('admin.adFormats.spec.clear')}
            </Button>
          ) : (
            <span />
          )}
          <Button disabled={isSaving} onClick={save}>
            {isSaving ? t('admin.adFormats.saving') : t('admin.adFormats.save')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
